"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { readClassesCalendarSessions } from "@/features/dashboard/admin/widgets/class-calendar-sessions";
import { useStudioAttendanceStats } from "@/features/dashboard/hooks/useStudioAttendanceStats";
import { useStudioOperationsSettings } from "@/features/dashboard/hooks/useStudioOperationsSettings";
import {
  useAccessToken,
  useClassesGetAdmin,
  useClassesGetByIds,
  useClassRoomsGet,
  useLocationsGet,
  usePoliciesGet,
  useSeasonsGet,
  useStudentsGet,
  useStudioClock,
  useStudioEnrollmentsGet,
  useTeachersGet,
  useTenantData,
} from "@/hooks";
import { calculateNextClassDate } from "@/lib/dashboard/calculate-next-class-date";
import { countTeachersWithAtLeastOneClass } from "@/lib/dashboard/count-teachers-with-classes";
import {
  computeLowEnrollmentFromClassesAndEnrollments,
  resolveClassEnrolledCount,
} from "@/lib/dashboard/low-enrollment-classes";
import { computeStudentsProgressFromEnrollments } from "@/lib/dashboard/students-progress";
import { buildStudioDashboardFetchPlan } from "@/lib/dashboard/studio-dashboard-fetch-plan";
import type { StudioCatalogPending } from "@/lib/dashboard/studio-dashboard-widget-pending";
import { computeTeacherLoadFromClassesAndTeachers } from "@/lib/dashboard/teacher-load";
import { fetchWidgetDataBatched } from "@/lib/dashboard/widgets-client";
import { paginatedTotalCount } from "@/lib/pagination/scrollPageUrl";
import { QueryKeys } from "@/lib/types";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import {
  createOpenApiConfiguration,
  enrollmentHasStatus,
  formatTimeTo12Hour,
  isActiveEnrollment,
  isPendingEnrollment,
} from "@/lib/utils";
import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import {
  calendarDaysBetweenYmd,
  getStudioClock,
  studioTodayDate,
} from "@/lib/utils/date/studio-clock";
import {
  EnrollmentStatus,
  type GetClassResponse,
  type GetEnrollmentResponse,
  LocationsApi,
  type PaginatedResultOfGetClassResponse,
  type PaginatedResultOfGetClassRoomResponse,
  type PaginatedResultOfGetLocationResponse,
  type PaginatedResultOfGetPolicyResponse,
  type PaginatedResultOfGetSeasonResponse,
  type PaginatedResultOfGetStudentResponse,
  type PaginatedResultOfGetTeacherResponse,
  SearchOperator,
  type SearchQueryRequest,
  SeasonsApi,
} from "@/openapi/generated";

/** Active students who currently have a class. One search, page size 1, totalCount only. */
const ACTIVE_STUDENTS_WITH_CLASSES_SEARCH: SearchQueryRequest = {
  criteria: [
    {
      field: "HasClasses",
      operator: SearchOperator.Equals,
      value: true,
    },
  ],
};

export type StudioDashboardWidgetProps = {
  topClasses: Array<{
    classId: number;
    className: string;
    enrollmentCount: number;
  }>;
  enrollmentStatus: {
    pending: number;
    enrolled: number;
    awaiting: number;
    total: number;
  };
  upcomingClasses: {
    today: UpcomingClassRow[];
    tomorrow: UpcomingClassRow[];
    next: UpcomingClassRow[];
  };
  teacherHasNoClassesAssigned?: boolean;
  lowEnrollmentClasses: ReturnType<
    typeof computeLowEnrollmentFromClassesAndEnrollments
  >;
  teacherLoad: ReturnType<typeof computeTeacherLoadFromClassesAndTeachers>;
  locationUsage: Array<{ locationId: number; classCount: number }>;
  classroomUsage: Array<{
    classRoomId: number;
    classCount: number;
    utilization: number;
  }>;
  seasons: Array<{
    seasonId: number;
    name: string;
    seasonStartDate: Date | null;
    seasonEndDate: Date | null;
    isActive: boolean;
  }>;
  locations: Array<{
    locationId: number;
    name: string;
    addressLine1?: string | null;
    addressLine2?: string | null;
    addressLine3?: string | null;
    city?: string | null;
    zipCode?: string | null;
    isActive?: boolean | null;
  }>;
  classrooms: Array<
    Record<string, unknown> & {
      locationId: number | null;
      locationName: string | null;
    }
  >;
  policies: Array<{
    policyId: number;
    name: string;
    isRequired?: boolean | null;
  }>;
  classes: GetClassResponse[];
  classCalendarSessions?: ReturnType<typeof readClassesCalendarSessions>;
  classCalendarError?: boolean;
  enrollments: GetEnrollmentResponse[];
  studentsProgress: ReturnType<typeof computeStudentsProgressFromEnrollments>;
  attendanceStats: {
    percentage: number;
    attended: number;
    total: number;
    missed: number;
  };
  attendanceStatsLoading?: boolean;
  attendanceStatsError?: boolean;
  onAttendanceStatsRetry?: () => void;
};

type UpcomingClassRow = {
  className: string;
  classId?: number;
  date: Date;
  time: string;
  isToday: boolean;
  isTomorrow: boolean;
  hoursUntil?: number;
};

export type StudioDashboardKpiDataBase = {
  totalStudents: number;
  activeStudents: number;
  totalClasses: number;
  activeClasses: number;
  pendingEnrollments: number;
  enrolledStudents: number;
  activeEnrollments: number;
  totalTeachers: number;
  activeTeachers: number;
  teachersWithClasses: number;
  totalSeasons: number;
  activeSeasons: number;
  totalLocations: number;
  activeLocations: number;
  totalClassrooms: number;
  activeClassrooms: number;
  totalPolicies: number;
  activePolicies: number;
};

type UseStudioDashboardWidgetDataOptions = {
  /** False while widget layout is still loading, so catalogs are not fetched early. */
  enabled?: boolean;
  kpiWidgets?: readonly DashboardWidgetId[];
  regularWidgets?: readonly DashboardWidgetId[];
  /** Daily workflow Class soon chip. Needs today's class schedule. */
  classStartingSoon?: boolean;
};

function queryIsBlocking(enabled: boolean, isPending: boolean) {
  return enabled && isPending;
}

export function useStudioDashboardWidgetData(
  options: UseStudioDashboardWidgetDataOptions = {},
) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const { timeZone } = useStudioClock();
  const fetchesReady = options.enabled !== false && !!accessToken;
  const plan = useMemo(
    () =>
      buildStudioDashboardFetchPlan({
        kpiWidgets: options.kpiWidgets ?? [],
        regularWidgets: options.regularWidgets ?? [],
        classStartingSoon: options.classStartingSoon === true,
      }),
    [options.classStartingSoon, options.kpiWidgets, options.regularWidgets],
  );
  const operationsEnabled = fetchesReady && plan.operationsSettings;
  const {
    lowEnrollmentThreshold,
    teacherLoadLightMax,
    teacherLoadPartTimeMax,
    teacherLoadFullTimeMax,
    isPending: isOperationsPending,
  } = useStudioOperationsSettings({ enabled: operationsEnabled });

  const attendanceEnabled = fetchesReady && plan.attendance;

  const {
    data: studioAttendanceAgg,
    isPending: isLoadingAttendanceStats,
    isError: isAttendanceStatsError,
    refetch: refetchAttendanceStats,
    isFetching: isFetchingAttendanceStats,
  } = useStudioAttendanceStats(attendanceEnabled);

  const studentTotalsEnabled = fetchesReady && plan.studentTotals;
  const { data: studentsData, isPending: isLoadingStudents } = useStudentsGet({
    pageSize: 1,
    enabled: studentTotalsEnabled,
  });

  const enrolledStudentsEnabled = fetchesReady && plan.enrolledStudents;
  const { data: enrolledStudentsData, isPending: isLoadingEnrolledStudents } =
    useStudentsGet({
      page: 1,
      pageSize: 1,
      active: true,
      searchQueryRequest: ACTIVE_STUDENTS_WITH_CLASSES_SEARCH,
      enabled: enrolledStudentsEnabled,
    });

  const calendarEnabled =
    fetchesReady &&
    (options.regularWidgets ?? []).includes(DashboardWidgetId.ClassesCalendar);
  const calendarQueryEnabled =
    calendarEnabled && tenantId != null && tenantId > 0;
  const {
    data: calendarWidgetData,
    isPending: isLoadingCalendar,
    isError: isCalendarError,
  } = useQuery({
    queryKey: [
      QueryKeys.WidgetData,
      tenantId,
      DashboardWidgetId.ClassesCalendar,
    ],
    enabled: calendarQueryEnabled,
    queryFn: async () => {
      if (!accessToken || tenantId == null) {
        throw new Error("Missing session or tenant for the classes calendar");
      }
      return fetchWidgetDataBatched(accessToken, tenantId, [
        DashboardWidgetId.ClassesCalendar,
      ]);
    },
    staleTime: 60_000,
  });

  const classCatalogEnabled = fetchesReady && plan.classCatalog;
  const {
    data: classCatalogData,
    isPending: isLoadingClassCatalog,
    isError: isErrorClassCatalog,
  } = useClassesGetAdmin({ pageSize: 1000, enabled: classCatalogEnabled });

  const classTotalCountEnabled = fetchesReady && plan.classTotalCount;
  const { data: classTotalCountData, isPending: isLoadingClassTotalCount } =
    useClassesGetAdmin({ pageSize: 1, enabled: classTotalCountEnabled });

  const classActiveCountEnabled = fetchesReady && plan.classActiveCount;
  const { data: activeClassesData, isPending: isLoadingActiveClasses } =
    useClassesGetAdmin({
      pageSize: 1,
      active: true,
      enabled: classActiveCountEnabled,
    });

  const teacherCatalogEnabled = fetchesReady && plan.teacherCatalog;
  const {
    data: teachersData,
    isPending: isLoadingTeachers,
    isError: isErrorTeachers,
  } = useTeachersGet({ pageSize: 1000, enabled: teacherCatalogEnabled });

  const seasonCatalogEnabled = fetchesReady && plan.seasonCatalog;
  const {
    data: seasonsData,
    isPending: isLoadingSeasons,
    isError: isErrorSeasons,
  } = useSeasonsGet({ enabled: seasonCatalogEnabled });

  const seasonActiveCountEnabled = fetchesReady && plan.seasonActiveCount;
  const { data: activeSeasonsData, isPending: isLoadingActiveSeasons } =
    useQuery({
      queryKey: [QueryKeys.Seasons, "scroll", 1, 1, true],
      queryFn: () =>
        new SeasonsApi(createOpenApiConfiguration()).apiSeasonsScrollGet({
          page: 1,
          pageSize: 1,
          active: true,
        }),
      enabled: seasonActiveCountEnabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const locationCatalogEnabled = fetchesReady && plan.locationCatalog;
  const {
    data: locationsData,
    isPending: isLoadingLocations,
    isError: isErrorLocations,
  } = useLocationsGet({ enabled: locationCatalogEnabled });

  const locationActiveCountEnabled = fetchesReady && plan.locationActiveCount;
  const { data: activeLocationsData, isPending: isLoadingActiveLocations } =
    useQuery({
      queryKey: [QueryKeys.Locations, "scroll", 1, 1, true],
      queryFn: () =>
        new LocationsApi(createOpenApiConfiguration()).apiLocationsScrollGet({
          page: 1,
          pageSize: 1,
          active: true,
        }),
      enabled: locationActiveCountEnabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const classroomCatalogEnabled = fetchesReady && plan.classroomCatalog;
  const {
    data: classroomsData,
    isPending: isLoadingClassrooms,
    isError: isErrorClassrooms,
  } = useClassRoomsGet({
    pageSize: 1000,
    enabled: classroomCatalogEnabled,
  });

  const classroomActiveCountEnabled = fetchesReady && plan.classroomActiveCount;
  const { data: activeClassroomsData, isPending: isLoadingActiveClassrooms } =
    useClassRoomsGet({
      pageSize: 1,
      active: true,
      enabled: classroomActiveCountEnabled,
    });

  const policyCatalogEnabled = fetchesReady && plan.policyCatalog;
  const {
    data: policiesData,
    isPending: isLoadingPolicies,
    isError: isErrorPolicies,
  } = usePoliciesGet({ pageSize: 1000, enabled: policyCatalogEnabled });

  const policyActiveCountEnabled = fetchesReady && plan.policyActiveCount;
  const { data: activePoliciesData, isPending: isLoadingActivePolicies } =
    usePoliciesGet({
      pageSize: 1,
      active: true,
      enabled: policyActiveCountEnabled,
    });

  const enrollmentCatalogEnabled = fetchesReady && plan.enrollmentCatalog;
  const {
    data: studioEnrollmentsData,
    isPending: isLoadingEnrollments,
    isError: isErrorEnrollments,
  } = useStudioEnrollmentsGet({ enabled: enrollmentCatalogEnabled });

  const classes = useMemo(() => {
    if (!classCatalogEnabled || isErrorClassCatalog || !classCatalogData)
      return [];
    const data = classCatalogData as PaginatedResultOfGetClassResponse;
    return data.items ?? [];
  }, [classCatalogData, classCatalogEnabled, isErrorClassCatalog]);

  const teachers = useMemo(() => {
    if (isErrorTeachers || !teachersData) return [];
    const data = teachersData as PaginatedResultOfGetTeacherResponse;
    return data.items ?? [];
  }, [teachersData, isErrorTeachers]);

  const seasons = useMemo(() => {
    if (isErrorSeasons || !seasonsData) return [];
    const data = seasonsData as PaginatedResultOfGetSeasonResponse;
    return data.items ?? [];
  }, [seasonsData, isErrorSeasons]);

  const locations = useMemo(() => {
    if (isErrorLocations || !locationsData) return [];
    const data = locationsData as PaginatedResultOfGetLocationResponse;
    return data.items ?? [];
  }, [locationsData, isErrorLocations]);

  const classrooms = useMemo(() => {
    if (isErrorClassrooms || !classroomsData) return [];
    const data = classroomsData as PaginatedResultOfGetClassRoomResponse;
    return data.items ?? [];
  }, [classroomsData, isErrorClassrooms]);

  const policies = useMemo(() => {
    if (isErrorPolicies || !policiesData) return [];
    const data = policiesData as PaginatedResultOfGetPolicyResponse;
    return data.items ?? [];
  }, [policiesData, isErrorPolicies]);

  const enrollments = useMemo(
    () => (isErrorEnrollments ? [] : (studioEnrollmentsData?.items ?? [])),
    [studioEnrollmentsData, isErrorEnrollments],
  );

  const activeEnrollments = useMemo(
    () => enrollments.filter(isActiveEnrollment),
    [enrollments],
  );

  const allClassIds = useMemo(
    () =>
      Array.from(new Set(classes.map((c) => Number(c.classId))))
        .filter((id) => !Number.isNaN(id))
        .sort((a, b) => a - b),
    [classes],
  );

  const classDetailsEnabled =
    fetchesReady && plan.classDetails && allClassIds.length > 0;
  const { classesMap: allClassesMap, isPending: isLoadingAllClassesDetails } =
    useClassesGetByIds({
      classIds: allClassIds,
      enabled: classDetailsEnabled,
    });

  const classesWithEnrollmentCounts = useMemo(() => {
    return classes.map((classItem) => {
      const classId = Number(classItem.classId);
      const detail = allClassesMap.get(classId);
      return detail ?? classItem;
    });
  }, [allClassesMap, classes]);

  const enrollmentStatus = useMemo(() => {
    const pending = activeEnrollments.filter(isPendingEnrollment).length;
    const enrolled = activeEnrollments.filter((e) =>
      enrollmentHasStatus(e, EnrollmentStatus.Enrolled),
    ).length;
    const awaiting = activeEnrollments.filter((e) =>
      enrollmentHasStatus(e, EnrollmentStatus.Awaiting),
    ).length;
    return {
      pending,
      enrolled,
      awaiting,
      total: activeEnrollments.length,
    };
  }, [activeEnrollments]);

  const topClasses = useMemo(() => {
    const classCounts: Record<number, number> = {};
    activeEnrollments.forEach((e) => {
      const classId = e._class?.classId;
      if (classId) {
        const id = Number(classId);
        classCounts[id] = (classCounts[id] || 0) + 1;
      }
    });

    return Object.entries(classCounts)
      .map(([classId, count]) => {
        const classDetails = allClassesMap.get(Number(classId));
        return {
          classId: Number(classId),
          className: classDetails?.name || `Class ${classId}`,
          enrollmentCount: count,
        };
      })
      .sort((a, b) => b.enrollmentCount - a.enrollmentCount)
      .slice(0, 5);
  }, [activeEnrollments, allClassesMap]);

  const upcomingClasses = useMemo(() => {
    const now = new Date();
    const clock = getStudioClock(now, timeZone);
    const clockToday = studioTodayDate(clock);
    const tomorrow = new Date(clockToday);
    tomorrow.setDate(clockToday.getDate() + 1);

    const byName = new Map<
      string,
      {
        className: string;
        classId?: number;
        date: Date;
        time: string;
        isToday: boolean;
        isTomorrow: boolean;
        hoursUntil: number;
      }
    >();

    allClassesMap.forEach((classDetails) => {
      if (!classDetails.classDays?.length) return;
      const firstDay = classDetails.classDays[0];
      if (!firstDay.startTime) return;

      const nextDateTimestamp = calculateNextClassDate(
        classDetails.classDays,
        now,
        timeZone,
      );
      if (!nextDateTimestamp) return;

      const classDate = new Date(nextDateTimestamp);
      const [hours, minutes] = firstDay.startTime.split(":").map(Number);
      classDate.setHours(hours, minutes, 0, 0);

      const classYmd = formatYmdLocal(classDate);
      const daysUntil = calendarDaysBetweenYmd(clock.ymd, classYmd);
      if (daysUntil < 0 || daysUntil > 7) return;

      const isToday = classDate.toDateString() === clockToday.toDateString();
      const isTomorrow = classDate.toDateString() === tomorrow.toDateString();
      const hoursUntil =
        (classDate.getTime() - now.getTime()) / (1000 * 60 * 60);

      const key = classDetails.name || `Class ${classDetails.classId}`;
      if (!byName.has(key) || byName.get(key)!.date > classDate) {
        byName.set(key, {
          className: classDetails.name || `Class ${classDetails.classId}`,
          classId: Number(classDetails.classId),
          date: classDate,
          time: formatTimeTo12Hour(firstDay.startTime),
          isToday,
          isTomorrow,
          hoursUntil: Math.round(hoursUntil),
        });
      }
    });

    return Array.from(byName.values()).sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );
  }, [allClassesMap, timeZone]);

  const classesToday = useMemo(
    () => upcomingClasses.filter((c) => c.isToday),
    [upcomingClasses],
  );
  const classesTomorrow = useMemo(
    () => upcomingClasses.filter((c) => c.isTomorrow),
    [upcomingClasses],
  );

  const lowEnrollmentClasses = useMemo(
    () =>
      computeLowEnrollmentFromClassesAndEnrollments(
        classesWithEnrollmentCounts,
        enrollments,
        lowEnrollmentThreshold,
      ),
    [classesWithEnrollmentCounts, enrollments, lowEnrollmentThreshold],
  );

  const teacherLoad = useMemo(
    () =>
      computeTeacherLoadFromClassesAndTeachers(classes, teachers, {
        teacherLoadLightMax,
        teacherLoadPartTimeMax,
        teacherLoadFullTimeMax,
      }),
    [
      classes,
      teacherLoadFullTimeMax,
      teacherLoadLightMax,
      teacherLoadPartTimeMax,
      teachers,
    ],
  );

  const locationUsage = useMemo(() => {
    const usageMap = new Map<number, number>();
    allClassesMap.forEach((classDetails) => {
      if (classDetails.location?.locationId) {
        const locationId = Number(classDetails.location.locationId);
        usageMap.set(locationId, (usageMap.get(locationId) || 0) + 1);
      }
    });
    return Array.from(usageMap.entries()).map(([locationId, classCount]) => ({
      locationId,
      classCount,
    }));
  }, [allClassesMap]);

  const classroomUsage = useMemo(() => {
    const usageMap = new Map<
      number,
      { classCount: number; totalCapacity: number; enrolled: number }
    >();

    allClassesMap.forEach((classDetails) => {
      if (classDetails.classRoom?.classRoomId) {
        const classRoomId = Number(classDetails.classRoom.classRoomId);
        const existing = usageMap.get(classRoomId) || {
          classCount: 0,
          totalCapacity: 0,
          enrolled: 0,
        };
        existing.classCount += 1;
        if (classDetails.maxNumberOfStudents) {
          existing.totalCapacity += Number(classDetails.maxNumberOfStudents);
        }
        existing.enrolled += resolveClassEnrolledCount(
          classDetails,
          enrollments,
        );
        usageMap.set(classRoomId, existing);
      }
    });

    return Array.from(usageMap.entries()).map(([classRoomId, data]) => {
      const capacity = data.totalCapacity || 1;
      const utilization = capacity > 0 ? (data.enrolled / capacity) * 100 : 0;
      return {
        classRoomId,
        classCount: data.classCount,
        utilization: Math.min(utilization, 100),
      };
    });
  }, [allClassesMap, enrollments]);

  const classroomsWithLocation = useMemo(
    () =>
      classrooms.map((classroom) => {
        const locationId = classroom.location?.locationId;
        const location = locationId
          ? locations.find((l) => l.locationId === locationId)
          : null;
        return {
          ...classroom,
          locationId: locationId != null ? Number(locationId) : null,
          locationName: location?.name || null,
        };
      }),
    [classrooms, locations],
  );

  const preparedSeasons = useMemo(
    () =>
      seasons.map((season) => ({
        seasonId: Number(season.seasonId),
        name: season.name || `Season ${season.seasonId}`,
        seasonStartDate: season.seasonStartDate
          ? new Date(season.seasonStartDate)
          : null,
        seasonEndDate: season.seasonEndDate
          ? new Date(season.seasonEndDate)
          : null,
        isActive: season.isActive ?? true,
      })),
    [seasons],
  );

  const preparedLocations = useMemo(
    () =>
      locations.map((location) => ({
        locationId: Number(location.locationId),
        name: location.name || `Location ${location.locationId}`,
        addressLine1: location.addressLine1,
        addressLine2: location.addressLine2,
        addressLine3: location.addressLine3,
        city: location.city,
        zipCode: location.zipCode,
        isActive: location.isActive,
      })),
    [locations],
  );

  const preparedPolicies = useMemo(
    () =>
      policies.map((policy) => ({
        policyId: Number(policy.policyId),
        name: policy.name || `Policy ${policy.policyId}`,
        isRequired: policy.isRequired,
      })),
    [policies],
  );

  const studentsProgress = useMemo(
    () => computeStudentsProgressFromEnrollments(enrollments),
    [enrollments],
  );

  const attendanceStats = useMemo(
    () =>
      studioAttendanceAgg ?? {
        percentage: 0,
        attended: 0,
        total: 0,
        missed: 0,
      },
    [studioAttendanceAgg],
  );

  const classesTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        (classCatalogEnabled ? classCatalogData : classTotalCountData) as
          | PaginatedResultOfGetClassResponse
          | undefined,
        classCatalogEnabled ? classes.length : 0,
      ),
    [
      classCatalogData,
      classCatalogEnabled,
      classTotalCountData,
      classes.length,
    ],
  );

  const activeClasses = useMemo(
    () =>
      paginatedTotalCount(
        activeClassesData as PaginatedResultOfGetClassResponse | undefined,
        classes.filter((c) => c.isActive !== false).length,
      ),
    [activeClassesData, classes],
  );

  const teachersTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        teachersData as PaginatedResultOfGetTeacherResponse | undefined,
        teachers.length,
      ),
    [teachersData, teachers.length],
  );

  const teachersWithClasses = useMemo(() => {
    if (classCatalogEnabled) {
      return countTeachersWithAtLeastOneClass(classes, teachers);
    }
    if (teacherCatalogEnabled) {
      return countTeachersWithAtLeastOneClass([], teachers);
    }
    return 0;
  }, [classCatalogEnabled, classes, teacherCatalogEnabled, teachers]);

  const seasonsTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        seasonsData as PaginatedResultOfGetSeasonResponse | undefined,
        seasons.length,
      ),
    [seasonsData, seasons.length],
  );

  const activeSeasons = useMemo(
    () =>
      paginatedTotalCount(
        activeSeasonsData as PaginatedResultOfGetSeasonResponse | undefined,
        seasons.filter((s) => s.isActive !== false).length,
      ),
    [activeSeasonsData, seasons],
  );

  const locationsTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        locationsData as PaginatedResultOfGetLocationResponse | undefined,
        locations.length,
      ),
    [locationsData, locations.length],
  );

  const activeLocations = useMemo(
    () =>
      paginatedTotalCount(
        activeLocationsData as PaginatedResultOfGetLocationResponse | undefined,
        locations.filter((l) => l.isActive !== false).length,
      ),
    [activeLocationsData, locations],
  );

  const classroomsTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        classroomsData as PaginatedResultOfGetClassRoomResponse | undefined,
        classrooms.length,
      ),
    [classroomsData, classrooms.length],
  );

  const activeClassrooms = useMemo(
    () =>
      paginatedTotalCount(
        activeClassroomsData as
          | PaginatedResultOfGetClassRoomResponse
          | undefined,
        classrooms.filter((c) => c.isActive !== false).length,
      ),
    [activeClassroomsData, classrooms],
  );

  const policiesTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        policiesData as PaginatedResultOfGetPolicyResponse | undefined,
        policies.length,
      ),
    [policiesData, policies.length],
  );

  const activePolicies = useMemo(
    () =>
      paginatedTotalCount(
        activePoliciesData as PaginatedResultOfGetPolicyResponse | undefined,
        policies.length,
      ),
    [activePoliciesData, policies.length],
  );

  const studioActiveEnrollmentCount = useMemo(
    () => enrollmentStatus.total,
    [enrollmentStatus.total],
  );

  const studentsTotalCount = useMemo(
    () =>
      paginatedTotalCount(
        studentsData as PaginatedResultOfGetStudentResponse | undefined,
        0,
      ),
    [studentsData],
  );

  const kpiDataBase = useMemo(
    (): StudioDashboardKpiDataBase => ({
      totalStudents: studentsTotalCount,
      activeStudents: 0,
      totalClasses: classesTotalCount,
      activeClasses,
      pendingEnrollments: enrollmentStatus.pending,
      enrolledStudents: paginatedTotalCount(
        enrolledStudentsData as PaginatedResultOfGetStudentResponse | undefined,
        0,
      ),
      activeEnrollments: studioActiveEnrollmentCount,
      totalTeachers: teachersTotalCount,
      activeTeachers: 0,
      teachersWithClasses,
      totalSeasons: seasonsTotalCount,
      activeSeasons,
      totalLocations: locationsTotalCount,
      activeLocations,
      totalClassrooms: classroomsTotalCount,
      activeClassrooms,
      totalPolicies: policiesTotalCount,
      activePolicies,
    }),
    [
      studentsTotalCount,
      enrolledStudentsData,
      classesTotalCount,
      activeClasses,
      enrollmentStatus,
      studioActiveEnrollmentCount,
      teachersTotalCount,
      teachersWithClasses,
      seasonsTotalCount,
      activeSeasons,
      locationsTotalCount,
      activeLocations,
      classroomsTotalCount,
      activeClassrooms,
      policiesTotalCount,
      activePolicies,
    ],
  );

  const widgetProps = useMemo((): StudioDashboardWidgetProps => {
    const upcomingNext = upcomingClasses
      .filter((c) => !c.isToday && !c.isTomorrow)
      .slice(0, 5);

    return {
      topClasses,
      enrollmentStatus,
      upcomingClasses: {
        today: classesToday,
        tomorrow: classesTomorrow,
        next: upcomingNext,
      },
      lowEnrollmentClasses,
      teacherLoad,
      locationUsage,
      classroomUsage,
      seasons: preparedSeasons,
      locations: preparedLocations,
      classrooms: classroomsWithLocation,
      policies: preparedPolicies,
      classes: Array.from(allClassesMap.values()),
      classCalendarSessions: calendarEnabled
        ? readClassesCalendarSessions(calendarWidgetData ?? [])
        : undefined,
      classCalendarError: calendarEnabled && isCalendarError,
      enrollments,
      studentsProgress,
      attendanceStats,
      attendanceStatsLoading:
        attendanceEnabled &&
        (isLoadingAttendanceStats ||
          (isFetchingAttendanceStats && !studioAttendanceAgg)),
      attendanceStatsError: attendanceEnabled && isAttendanceStatsError,
      onAttendanceStatsRetry: attendanceEnabled
        ? () => {
            void refetchAttendanceStats();
          }
        : undefined,
    };
  }, [
    topClasses,
    enrollmentStatus,
    classesToday,
    classesTomorrow,
    upcomingClasses,
    lowEnrollmentClasses,
    teacherLoad,
    locationUsage,
    classroomUsage,
    preparedSeasons,
    preparedLocations,
    classroomsWithLocation,
    preparedPolicies,
    allClassesMap,
    calendarEnabled,
    calendarWidgetData,
    isCalendarError,
    enrollments,
    studentsProgress,
    attendanceStats,
    attendanceEnabled,
    isLoadingAttendanceStats,
    isFetchingAttendanceStats,
    studioAttendanceAgg,
    isAttendanceStatsError,
    refetchAttendanceStats,
  ]);

  const pending: StudioCatalogPending = {
    studentTotals: queryIsBlocking(studentTotalsEnabled, isLoadingStudents),
    enrolledStudents: queryIsBlocking(
      enrolledStudentsEnabled,
      isLoadingEnrolledStudents,
    ),
    classCatalog: queryIsBlocking(classCatalogEnabled, isLoadingClassCatalog),
    classDetails: queryIsBlocking(
      classDetailsEnabled,
      isLoadingAllClassesDetails,
    ),
    classTotalCount: queryIsBlocking(
      classTotalCountEnabled,
      isLoadingClassTotalCount,
    ),
    classActiveCount: queryIsBlocking(
      classActiveCountEnabled,
      isLoadingActiveClasses,
    ),
    teacherCatalog: queryIsBlocking(teacherCatalogEnabled, isLoadingTeachers),
    seasonCatalog: queryIsBlocking(seasonCatalogEnabled, isLoadingSeasons),
    seasonActiveCount: queryIsBlocking(
      seasonActiveCountEnabled,
      isLoadingActiveSeasons,
    ),
    locationCatalog: queryIsBlocking(
      locationCatalogEnabled,
      isLoadingLocations,
    ),
    locationActiveCount: queryIsBlocking(
      locationActiveCountEnabled,
      isLoadingActiveLocations,
    ),
    classroomCatalog: queryIsBlocking(
      classroomCatalogEnabled,
      isLoadingClassrooms,
    ),
    classroomActiveCount: queryIsBlocking(
      classroomActiveCountEnabled,
      isLoadingActiveClassrooms,
    ),
    policyCatalog: queryIsBlocking(policyCatalogEnabled, isLoadingPolicies),
    policyActiveCount: queryIsBlocking(
      policyActiveCountEnabled,
      isLoadingActivePolicies,
    ),
    enrollmentCatalog: queryIsBlocking(
      enrollmentCatalogEnabled,
      isLoadingEnrollments,
    ),
    operationsSettings: queryIsBlocking(operationsEnabled, isOperationsPending),
    calendar: queryIsBlocking(calendarQueryEnabled, isLoadingCalendar),
  };

  const isLoading = Object.values(pending).some(Boolean);

  return {
    isLoading,
    pending,
    widgetProps,
    kpiDataBase,
    enrollmentStatus,
    classesToday,
    classesTomorrow,
    upcomingClasses,
  };
}
